/**
 * RegistrationForm Component
 *
 * Registration form for new paid users. Captures name and email,
 * with plan already selected from previous step.
 *
 * Project: P07 - Stripe Integration
 * Sprint: S02 - Registration Flow
 * Work Unit: WU02 - Registration Form & API
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { ArrowLeft, Mail, User } from 'lucide-react';

import {
  Button,
  Input,
  Form,
  Alert,
  Link,
  Card,
  CardBody,
  CardHeader,
} from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import type { PlanType, BillingInterval } from '@enfomentis/shared/types';
import { Brand, Plans } from '@enfomentis/shared/config';

// =============================================================================
// Types
// =============================================================================

interface RegistrationFormProps {
  /** Selected plan type from previous step */
  planType: PlanType;
  /** Selected billing interval from previous step */
  billingInterval: BillingInterval;
  /** Callback to go back to plan selection */
  onBack?: (() => void) | undefined;
}

interface RegistrationResponse {
  success: boolean;
  registrationId?: string;
  email?: string;
  message?: string;
  error?: string;
  code?: string;
}

// =============================================================================
// API Helper
// =============================================================================

async function createRegistration(data: {
  email: string;
  fullName: string;
  planType: PlanType;
  billingInterval: BillingInterval;
}): Promise<RegistrationResponse> {
  const response = await fetch(
    `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/create-registration`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env['VITE_SUPABASE_ANON_KEY'] as string,
      },
      body: JSON.stringify(data),
    }
  );

  return response.json();
}

// =============================================================================
// Component
// =============================================================================

export function RegistrationForm({
  planType,
  billingInterval,
  onBack,
}: RegistrationFormProps): React.JSX.Element {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Get plan details for display
  const plan = Plans[planType];
  const price = billingInterval === 'month' ? plan.monthly : plan.yearly;

  // Validation functions
  const validateName = useCallback((value: string): string | null => {
    if (!value.trim()) return 'Full name is required';
    if (value.trim().length < 2) return 'Name must be at least 2 characters';
    if (value.trim().length > 100) return 'Name must be less than 100 characters';
    return null;
  }, []);

  const validateEmail = useCallback((value: string): string | null => {
    if (!value) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return null;
  }, []);

  // Validation state
  const nameError = useMemo(() => {
    if (!hasSubmitted && !nameTouched) return null;
    return validateName(formData.fullName);
  }, [formData.fullName, hasSubmitted, nameTouched, validateName]);

  const emailError = useMemo(() => {
    if (!hasSubmitted && !emailTouched) return null;
    return validateEmail(formData.email);
  }, [formData.email, hasSubmitted, emailTouched, validateEmail]);

  // Form submission
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setHasSubmitted(true);
    setServerErrors({});
    setSuccessMessage(null);

    // Client-side validation
    const nameValidation = validateName(formData.fullName);
    const emailValidation = validateEmail(formData.email);

    if (nameValidation || emailValidation) {
      return; // Don't submit if validation fails
    }

    setIsSubmitting(true);

    try {
      const result = await createRegistration({
        email: formData.email.trim().toLowerCase(),
        fullName: formData.fullName.trim(),
        planType,
        billingInterval,
      });

      if (result.success) {
        setSuccessMessage(
          result.message || 'Please check your email to verify your account.'
        );
        // Navigate to check-email page after short delay
        setTimeout(() => {
          navigate('/check-email', {
            state: { email: formData.email },
            replace: true,
          });
        }, 2000);
      } else {
        // Map error codes to field errors
        switch (result.code) {
          case 'EMAIL_EXISTS':
            setServerErrors({
              email: 'An account with this email already exists',
            });
            break;
          case 'VALIDATION_ERROR':
            setServerErrors({ general: result.error || 'Validation failed' });
            break;
          case 'RATE_LIMITED':
            setServerErrors({
              general: 'Too many attempts. Please try again later.',
            });
            break;
          default:
            setServerErrors({
              general: result.error || 'Registration failed. Please try again.',
            });
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      setServerErrors({
        general: 'Network error. Please check your connection and try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout hideFooter={true}>
      <Card
        className="items-center justify-center h-full"
        radius="none"
        shadow="none"
      >
        <CardHeader className="flex flex-col max-w-md mx-auto pt-10 pb-4">
          {/* Back button */}
          {onBack && (
            <Button
              variant="light"
              size="sm"
              startContent={<ArrowLeft className="w-4 h-4" />}
              onPress={onBack}
              className="self-start mb-4"
            >
              Back to plans
            </Button>
          )}

          {/* Header */}
          <div className="text-center w-full">
            <h1 className="text-2xl font-bold text-text-primary">
              Create your account
            </h1>
            <p className="text-text-secondary mt-2">
              You&apos;re signing up for {plan.name} ({price.displayPrice}/
              {billingInterval === 'month' ? 'mo' : 'yr'})
            </p>
          </div>
        </CardHeader>

        <CardBody className="flex max-w-md mx-auto p-6">
          {/* Success message */}
          {successMessage && (
            <Alert
              color="success"
              variant="flat"
              description={successMessage}
              className="mb-6 grow-0"
            />
          )}

          <Form
            validationBehavior="aria"
            validationErrors={serverErrors}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Full Name Input */}
            <Input
              type="text"
              label="Full Name"
              placeholder="Enter your full name"
              value={formData.fullName}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, fullName: value }))
              }
              onBlur={() => setNameTouched(true)}
              validate={validateName}
              isRequired
              autoComplete="name"
              startContent={
                <User className="w-4 h-4 text-default-400 pointer-events-none flex-shrink-0" />
              }
              errorMessage={serverErrors['fullName'] || nameError}
              isInvalid={!!serverErrors['fullName'] || !!nameError}
              classNames={{
                input: 'text-base',
              }}
            />

            {/* Email Input */}
            <Input
              type="email"
              label="Email"
              placeholder="Enter your email"
              value={formData.email}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, email: value }))
              }
              onBlur={() => setEmailTouched(true)}
              validate={validateEmail}
              isRequired
              isClearable
              autoComplete="email"
              startContent={
                <Mail className="w-4 h-4 text-default-400 pointer-events-none flex-shrink-0" />
              }
              errorMessage={serverErrors['email'] || emailError}
              isInvalid={!!serverErrors['email'] || !!emailError}
              classNames={{
                input: 'text-base',
              }}
            />

            {/* General Error Display */}
            {serverErrors['general'] && (
              <Alert
                color="warning"
                variant="flat"
                description={serverErrors['general']}
              />
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              color="primary"
              size="lg"
              isLoading={isSubmitting}
              isDisabled={!!successMessage}
              className="w-full"
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>

            {/* Login link */}
            <p className="text-sm text-text-secondary text-center">
              Already have an account?{' '}
              <Link
                as={RouterLink}
                {...({ to: '/login' } as { to: string })}
                color="primary"
                size="sm"
              >
                Sign in
              </Link>
            </p>

            {/* Terms notice */}
            <p className="text-xs text-text-tertiary text-center">
              By creating an account, you agree to our{' '}
              <Link
                href={`${Brand.helpUrl}/terms`}
                isExternal
                size="sm"
                className="text-xs"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                href={`${Brand.helpUrl}/privacy`}
                isExternal
                size="sm"
                className="text-xs"
              >
                Privacy Policy
              </Link>
            </p>
          </Form>
        </CardBody>

      </Card>
    </AppLayout>
  );
}
